import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/shared/api/comments";
import { filterDocIds } from "@/shared/api/tags";
import { AdminList, AdminListRow } from "@/modules/shell/AdminList";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

export function InboxPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const inboxQuery = useQuery({
    queryKey: queryKeys.inbox.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listNotifications(companySlug!),
  });

  const assignedQuery = useQuery({
    queryKey: ["inbox", companySlug, "assignedToMe"],
    enabled: Boolean(companySlug),
    queryFn: async () => {
      const [so, po] = await Promise.all([
        filterDocIds(companySlug!, {
          docType: "sales_order",
          assignedToMe: true,
        }),
        filterDocIds(companySlug!, {
          docType: "purchase_order",
          assignedToMe: true,
        }),
      ]);
      return {
        salesOrderCount: so.docIds.length,
        purchaseOrderCount: po.docIds.length,
      };
    },
  });
  const errorMsg = queryErrorMessage(inboxQuery.error, inboxQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  async function reload() {
    if (!companySlug) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.inbox.list(companySlug),
    });
  }

  if (!companySlug) return null;

  const rows = inboxQuery.data?.notifications ?? [];
  const unreadCount = inboxQuery.data?.unreadCount ?? 0;
  const assignedCounts = assignedQuery.data;
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  return (
      <PageFrame>
      <PageHeader
        title="Inbox"
        description={
          unreadCount > 0
            ? `Mentions, approvals, and messages · ${unreadCount} unread`
            : "Mentions, approvals, and in-app messages"
        }
        actions={
          unreadCount > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void markAllNotificationsRead(companySlug)
                  .then(() => reload())
                  .catch((err: unknown) =>
                    setError(err instanceof Error ? err.message : "Failed"),
                  );
              }}
            >
              Mark all read
            </Button>
          ) : undefined
        }
      />

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      {!assignedQuery.isPending &&
      assignedCounts &&
      (assignedCounts.salesOrderCount > 0 ||
        assignedCounts.purchaseOrderCount > 0) ? (
        <section
          className="mt-8 rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)]/60 px-4 py-4 shadow-[var(--shadow-soft)]"
          aria-labelledby="inbox-assigned-heading"
        >
          <h2
            id="inbox-assigned-heading"
            className="m-0 text-sm font-semibold text-[var(--color-foreground)]"
          >
            Assigned to me
          </h2>
          <p className="mt-1 mb-0 text-xs text-[var(--color-muted)]">
            Open orders where you are on the assignee list.
          </p>
          <ul className="m-0 mt-3 list-none space-y-2 p-0">
            {assignedCounts.salesOrderCount > 0 ? (
              <li>
                <Link
                  to={`/c/${companySlug}/sales/orders?assignee=mine`}
                  className="text-sm font-medium text-[var(--color-primary)] no-underline hover:underline"
                >
                  Open sales orders
                </Link>
                <span className="ml-1.5 text-xs text-[var(--color-muted)] tabular-nums">
                  ({assignedCounts.salesOrderCount})
                </span>
              </li>
            ) : null}
            {assignedCounts.purchaseOrderCount > 0 ? (
              <li>
                <Link
                  to={`/c/${companySlug}/purchasing/orders?assignee=mine`}
                  className="text-sm font-medium text-[var(--color-primary)] no-underline hover:underline"
                >
                  Open purchase orders
                </Link>
                <span className="ml-1.5 text-xs text-[var(--color-muted)] tabular-nums">
                  ({assignedCounts.purchaseOrderCount})
                </span>
              </li>
            ) : null}
          </ul>
        </section>
      ) : null}

      {inboxQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading inbox…
        </Alert>
      )}

      {!inboxQuery.isPending && rows.length === 0 && !displayError ? (
        <div className="mt-8">
          <EmptyState
            title="No messages yet"
            description="Mentions, approval requests, and in-app notices show up here."
          />
        </div>
      ) : !inboxQuery.isPending && rows.length > 0 ? (
        <AdminList>
          {rows.map((n) => (
            <AdminListRow
              key={n.id}
              className={`p-0 ${n.readAt ? "opacity-70" : ""}`}
            >
              <button
                type="button"
                className="flex w-full flex-col items-start gap-1 px-4 py-3.5 text-left transition-colors hover:bg-[var(--color-background)]/70"
                onClick={() => {
                  void (async () => {
                    if (!n.readAt) {
                      await markNotificationRead(companySlug, n.id);
                    }
                    if (n.linkPath) navigate(n.linkPath);
                    else await reload();
                  })().catch((err: unknown) =>
                    setError(err instanceof Error ? err.message : "Failed"),
                  );
                }}
              >
                <span className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
                  {n.type}
                  {!n.readAt ? (
                    <Badge variant="primary">unread</Badge>
                  ) : null}
                </span>
                <span className="text-sm font-medium">{n.title}</span>
                {n.body && (
                  <span className="text-xs text-[var(--color-muted)]">
                    {n.body}
                  </span>
                )}
                <span className="text-[10px] text-[var(--color-muted)]">
                  {new Date(n.createdAt).toLocaleString("en-PH", {
                    timeZone: "Asia/Manila",
                  })}
                </span>
              </button>
            </AdminListRow>
          ))}
        </AdminList>
      ) : null}

      <p className="mt-8">
        <Button variant="ghost" asChild>
          <Link to={`/c/${companySlug}`} className="no-underline">
            ← Company
          </Link>
        </Button>
      </p>
    </PageFrame>
  );
}
